/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
/**
 * syncEngine.js — Sincronizzazione resiliente OUTBOX → server.
 *
 * - Ascolta `online`/`offline` e `visibilitychange`.
 * - Svuota la coda `outbox` (IndexedDB) in ordine FIFO con retry esponenziale.
 * - Ogni operazione è idempotente lato server grazie all'header
 *   `X-Idempotency-Key` (= id outbox).
 * - `purge_local: true` sull'operazione → dopo la conferma del server il record
 *   locale di riferimento viene ELIMINATO (memoria del dispositivo svuotata).
 * - Espone uno stato osservabile per l'indicatore UI (OfflineStatusBadge).
 *
 * Funziona con qualunque backend (MongoDB o PostgreSQL/Supabase via
 * `DATABASE_URL`) perché parla solo con le API REST esistenti.
 */
import api from '@/lib/api';
import {
  getPendingOutbox, removeOutbox, markOutboxError, markRecordSynced, setMeta, getOfflineStats, deleteRecord,
  retryFailedOutbox, OUTBOX_MAX_ATTEMPTS,
} from '@/lib/offlineDb';

const MAX_ATTEMPTS = OUTBOX_MAX_ATTEMPTS;

class SyncEngine {
  constructor() {
    this.state = {
      online: typeof navigator === 'undefined' ? true : navigator.onLine,
      syncing: false,
      pending: 0,
      failed: 0, // operazioni che hanno esaurito i tentativi (richiedono retry manuale)
      lastSyncAt: null,
      lastError: null,
      lastPushed: 0, // record inviati nell'ultimo flush (per il toast "sincronizzati N elementi")
    };
    this.listeners = new Set();
    this._timer = null;
    this._started = false;
  }

  subscribe(fn) {
    this.listeners.add(fn);
    fn(this.state);
    return () => this.listeners.delete(fn);
  }

  _set(patch) {
    this.state = { ...this.state, ...patch };
    this.listeners.forEach((fn) => { try { fn(this.state); } catch (e) { /* ignore */ } });
  }

  /** Avvia i listener globali (idempotente). */
  start() {
    if (this._started || typeof window === 'undefined') return;
    this._started = true;
    window.addEventListener('online', () => { this._set({ online: true }); this.flush(); });
    window.addEventListener('offline', () => this._set({ online: false }));
    document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') this.flush(); });
    // Job periodico di sicurezza (ogni 60s) + primo flush.
    this._timer = setInterval(() => this.flush(), 60_000);
    this.refreshPending();
    this.flush();
  }

  async refreshPending() {
    const stats = await getOfflineStats();
    this._set({ pending: stats.pending, failed: stats.failed || 0 });
    return stats.pending;
  }

  /** Azzera i tentativi delle operazioni fallite e rilancia la sync. */
  async retryFailed() {
    try { await retryFailedOutbox(); } catch (e) { /* ignore */ }
    await this.flush();
  }

  /** Invia tutte le operazioni in coda. Sicuro da chiamare più volte. */
  async flush() {
    if (this.state.syncing || (typeof navigator !== 'undefined' && navigator.onLine === false)) return;
    let items;
    try { items = await getPendingOutbox(); } catch (e) { return; }
    if (items.length === 0) { this._set({ pending: 0, failed: 0 }); return; }

    this._set({ syncing: true, lastError: null });
    // try/finally: qualunque eccezione imprevista NON deve lasciare `syncing=true`
    // (altrimenti la coda resterebbe bloccata fino al reload della pagina).
    try {
      await this._drain(items);
    } catch (e) {
      this._set({ lastError: e?.message || 'Errore di sincronizzazione' });
    } finally {
      if (this.state.syncing) this._set({ syncing: false });
    }
  }

  async _drain(items) {
    let pushed = 0;
    for (const op of items) {
      if (!navigator.onLine) break;
      if ((op.attempts || 0) >= MAX_ATTEMPTS) continue; // lasciato in coda per revisione manuale
      try {
        const method = (op.method || 'POST').toLowerCase();
        const res = await api.request({
          url: op.path,
          method,
          data: op.body,
          headers: { 'X-Idempotency-Key': op.id },
        });
        if (op.ref_store && op.ref_id) {
          if (op.purge_local) await deleteRecord(op.ref_store, op.ref_id).catch(() => {});
          else await markRecordSynced(op.ref_store, op.ref_id, res?.data?.id || res?.data?.note?.id || res?.data?.item?.id);
        }
        await removeOutbox(op.id);
        pushed += 1;
      } catch (e) {
        const status = e?.response?.status;
        // 4xx (eccetto 401/408/429) = errore definitivo del payload: non ritentare all'infinito.
        if (status && status >= 400 && status < 500 && ![401, 408, 429].includes(status)) {
          await markOutboxError(op.id, e);
          // Dopo MAX_ATTEMPTS resta in coda ma non blocca gli altri.
        } else {
          await markOutboxError(op.id, e);
          this._set({ lastError: e?.message || 'Errore di rete' });
          break; // rete instabile: riprova al prossimo evento online
        }
      }
    }
    const pending = await this.refreshPending();
    const now = new Date().toISOString();
    await setMeta('last_sync_at', now).catch(() => {});
    this._set({ syncing: false, lastSyncAt: now, pending, lastPushed: pushed });
  }
}

export const syncEngine = new SyncEngine();
export default syncEngine;
